import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { emberApi, type ChatsResponse } from './api';
import { chatArrivals, type ChatArrival } from './chats';
import { clientConfig } from './client-config';
import { KNOCKS_CHANGED_EVENT } from './components/use-knocks';

/**
 * The person's Chats list, one copy per page (2026-10-20; GET /api/me/chats).
 * Everything that reads it — the list, the back control's count, the banner,
 * the account menu's row, the night's clock — reads this one store. It is
 * read once the person is signed in (`useChatsLive`, on every page since
 * 2026-10-26), when a place that shows it opens and the last read is older
 * than chat_poll_seconds (`useChatsOnce`: the Chats page and pane, home, the
 * account menu — 2026-10-31 founder: a page reads fresh when it is shown, so
 * a read that failed or a change nobody announced is put right by coming to
 * it), when the tab comes back to the front, and whenever the live channel
 * says the list changed (live.ts `chats`: a room line, a reaction, a letter,
 * Ember's reply, an answer, a join, a leave) or a letter moved — no poll
 * since 2026-10-21 (every chat_poll_seconds until then). A page whose facts
 * move with the list (Meet people, an introduction, a room's page, a topic
 * room, a check-in, a room's card in Ember's conversation) reads itself again
 * on each read.
 *
 * Each read is compared with the one before (chats.ts chatArrivals): what
 * landed in between is handed on as a numbered batch, which the banner and
 * the list's row highlight act on once. The first read, and the one after
 * the tab comes back to the front, only set the baseline — a line that
 * landed while nobody was looking is in the counts, not in a banner saying
 * "now".
 */

export interface ChatsSnapshot {
  data: ChatsResponse | null;
  /** What the last read brought, numbered: a reader acts on each batch once. */
  arrivals: { seq: number; list: ChatArrival[] };
  /** The last read failed (the list keeps the one before it). */
  failed: boolean;
}

let snapshot: ChatsSnapshot = { data: null, arrivals: { seq: 0, list: [] }, failed: false };
const listeners = new Set<() => void>();
let live = 0;
let inFlight: Promise<void> | null = null;
let baseline = true;
let readAt = 0;

function emit(next: ChatsSnapshot): void {
  snapshot = next;
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

const getSnapshot = (): ChatsSnapshot => snapshot;

let again = false;

/**
 * Read the list now, one read at a time. An ask while one is on the wire reads once more after it (2026-10-26): the
 * read already out may have left before the change it was asked for, and its line would wait for the next change.
 */
export function refreshChats(): Promise<void> {
  if (inFlight) { again = true; return inFlight; }
  inFlight = emberApi.chats()
    .then((next) => {
      const prev = baseline ? null : snapshot.data;
      baseline = false;
      readAt = Date.now();
      const list = chatArrivals(prev, next);
      emit({ data: next, arrivals: list.length ? { seq: snapshot.arrivals.seq + 1, list } : snapshot.arrivals, failed: false });
    })
    // A read that fails leaves the last one standing; the next change, return to the tab or opening of the list reads again.
    .catch(() => { if (!snapshot.failed) emit({ ...snapshot, failed: true }); })
    .finally(() => {
      inFlight = null;
      if (again) { again = false; void refreshChats(); }
    });
  return inFlight;
}

function onVisible(): void {
  if (live <= 0 || document.visibilityState !== 'visible') return;
  baseline = true;
  void refreshChats();
}

/** The list changed (the live channel, a knock): read it again when anyone shows it. */
export function chatsChanged(): void {
  if (live > 0 || snapshot.data) void refreshChats();
}

/** The list as last read, re-rendering on every read. */
export function useChatsSnapshot(): ChatsSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Keep the list current while a chat surface is on screen (`enabled`: signed in, and the surface is up): one read as
 * it opens (the baseline), one on each return to the tab, one on every change the live channel announces.
 */
export function useChatsLive(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined;
    live += 1;
    if (live === 1) {
      baseline = true;
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener(KNOCKS_CHANGED_EVENT, chatsChanged);
      void refreshChats();
    }
    return () => {
      live -= 1;
      if (live > 0) return;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(KNOCKS_CHANGED_EVENT, chatsChanged);
    };
  }, [enabled]);
}

/** The list for a place that shows it (the Chats page and pane, home, the account menu): one read as it is drawn when the last is older than a poll. */
export function useChatsOnce(enabled: boolean): ChatsSnapshot {
  const snap = useChatsSnapshot();
  useEffect(() => {
    if (!enabled) return;
    if (!snapshot.data || Date.now() - readAt > clientConfig().chat_poll_seconds * 1000) void refreshChats();
  }, [enabled]);
  return snap;
}

/** Ember's turns were just seen on a screen: its row's unread is nought until the next read says otherwise. */
function markEmberSeen(): void {
  const data = snapshot.data;
  if (!data || !data.ember.unread) return;
  emit({ ...snapshot, data: { ...data, ember: { ...data.ember, unread: 0 } } });
}

/**
 * Tell the edge how far the person's conversation with Ember has been on screen (POST
 * /api/conversations/:id/seen): on open and after new turns appear, a second after they settle, only while the
 * tab is in front, and only forward. `turn` is the newest of the edge's turns the thread shows.
 */
export function useEmberSeen(conversationId: string | null, turn: number | null, active: boolean): void {
  const sent = useRef({ id: '', turn: -1 });
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible');
  useEffect(() => {
    const on = (): void => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);
  useEffect(() => {
    if (!active || !visible || !conversationId || turn === null || turn < 0) return undefined;
    if (sent.current.id === conversationId && sent.current.turn >= turn) return undefined;
    const t = window.setTimeout(() => {
      const before = sent.current;
      sent.current = { id: conversationId, turn };
      emberApi.conversationSeen(conversationId, turn)
        .then(markEmberSeen)
        // Not said: the next turn on screen says it again.
        .catch(() => { sent.current = before; });
    }, 1000);
    return () => window.clearTimeout(t);
  }, [active, visible, conversationId, turn]);
}
